import React from 'react';
import ArrowBackIosNewRoundedIcon from '@mui/icons-material/ArrowBackIosNewRounded';
import EventNoteOutlinedIcon from '@mui/icons-material/EventNoteOutlined';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Paper, TextField, Typography } from '@mui/material';
import { format } from 'date-fns';
import type { Appointment } from '@/types';
import { getSessionNotesFromAppointment, postAppointmentNote } from '@/lib/api/appointments';
import { extractErrorMessage, showErrorToast, showSuccessToast } from '@/utils/toast-utils';
import InlineFeedback from '@/components/shared/InlineFeedback';
import {
  canJoinAppointment,
  getVideoMeetingPlatformLabel,
  shouldHideAppointmentJoinRescheduleCancel,
} from '@/utils/appointments';
import { formatUnderscoreDisplayLabel } from '@/utils/display-labels';
import googleMeetIcon from '@/assets/images/appointments/google_meet.svg';

export type ClientAppointmentSessionDetailViewProps = {
  appointment: Appointment;
  /** True when opened from Past Sessions or the session has already ended. */
  isPastSession: boolean;
  onBack: () => void;
  onScheduleSession: () => void;
  onViewAllSessions: () => void;
  onReschedule: () => void;
  onCancel: () => void | Promise<void>;
  /** Optional: e.g. refetch appointment in parent after notes change. */
  onNotesUpdated?: () => void | Promise<void>;
};

function parseAppointmentTimes(appointment: Appointment): {
  start: Date;
  end: Date;
  title: string;
  description: string;
  meetingLink: string;
  statusRaw: string | undefined;
  tagList: string[];
} {
  const raw = appointment as unknown as {
    title?: unknown;
    description?: unknown;
    startTime?: unknown;
    start_time?: unknown;
    endTime?: unknown;
    end_time?: unknown;
    meetingLink?: unknown;
    meeting_link?: unknown;
    status?: unknown;
    tags?: unknown;
  };

  const tagList = Array.isArray(raw.tags)
    ? raw.tags.map((t) => String(t || '').trim()).filter(Boolean).slice(0, 6)
    : [];

  const title = String(raw.title || appointment.type || 'Session').trim() || 'Session';
  const description = String(raw.description || appointment.notes || '').trim();

  const startSrc = raw.startTime ?? raw.start_time ?? appointment.date;
  const start = startSrc instanceof Date ? startSrc : new Date(String(startSrc));

  const endSrc = raw.endTime ?? raw.end_time;
  let end: Date;
  if (endSrc) {
    end = endSrc instanceof Date ? endSrc : new Date(String(endSrc));
  } else {
    end = new Date(start.getTime() + Math.max(1, appointment.duration || 60) * 60_000);
  }

  const meetingLink = String(raw.meetingLink || raw.meeting_link || '').trim();
  const statusRaw =
    raw.status != null
      ? String(raw.status)
      : appointment.status != null
        ? String(appointment.status)
        : undefined;

  return { start, end, title, description, meetingLink, statusRaw, tagList };
}

function getModeLabel(appointment: Appointment): string {
  const raw = appointment as unknown as { appointmentType?: unknown; mode?: unknown; location?: unknown };
  const type = String(raw.appointmentType || raw.mode || appointment.mode || '').toLowerCase();
  const location = String(raw.location || '').toLowerCase();
  if (type === 'offline' || type === 'in_person' || location.includes('person')) {
    return 'In-Person';
  }
  return 'Video Call';
}

function formatNoteTimestamp(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return format(d, 'd MMM yyyy, h:mm a');
}

export const ClientAppointmentSessionDetailView: React.FC<ClientAppointmentSessionDetailViewProps> = ({
  appointment,
  isPastSession,
  onBack,
  onScheduleSession,
  onViewAllSessions,
  onReschedule,
  onCancel,
  onNotesUpdated,
}) => {
  const appointmentId = appointment.id;

  const [addNoteOpen, setAddNoteOpen] = React.useState(false);
  const [noteDraft, setNoteDraft] = React.useState('');
  const [savingNote, setSavingNote] = React.useState(false);
  const [noteFeedback, setNoteFeedback] = React.useState('');

  const sortedNotes = React.useMemo(() => {
    const list = getSessionNotesFromAppointment(appointment);
    return [...list].sort((a, b) => {
      const ta = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const tb = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return tb - ta;
    });
  }, [appointment]);

  const handleSaveNote = async () => {
    const text = noteDraft.trim();
    if (!text) return;
    setNoteFeedback('');
    setSavingNote(true);
    try {
      await postAppointmentNote(appointmentId, { text });
      setNoteDraft('');
      setNoteFeedback('');
      setAddNoteOpen(false);
      await onNotesUpdated?.();
    } catch (error) {
      setNoteFeedback(extractErrorMessage(error) || 'Failed to save note.');
    } finally {
      setSavingNote(false);
    }
  };

  const { start, end, title, description, meetingLink, statusRaw, tagList } = parseAppointmentTimes(appointment);
  const modeLabel = getModeLabel(appointment);
  const durationMin = appointment.duration || Math.max(1, Math.round((end.getTime() - start.getTime()) / 60_000));
  const hideLifecycle =
    isPastSession ||
    shouldHideAppointmentJoinRescheduleCancel(statusRaw, end, {
      startAt: start,
      durationMinutes: durationMin,
    });
  const showJoin =
    Boolean(meetingLink) &&
    canJoinAppointment(statusRaw, start, end, { startAt: start, durationMinutes: durationMin });
  const meetingPlatformLabel = meetingLink ? getVideoMeetingPlatformLabel(meetingLink) : '';
  const isGoogleMeetLink = meetingPlatformLabel === 'Google Meet';

  const handleCopyMeetingLink = async () => {
    if (!meetingLink) return;
    try {
      await navigator.clipboard.writeText(meetingLink);
      showSuccessToast('Link copied', 'Meeting link copied to clipboard.');
    } catch {
      showErrorToast(new Error('Copy failed'), 'Could not copy meeting link');
    }
  };

  const statusDisplay =
    statusRaw && statusRaw.trim().length > 0
      ? statusRaw.trim().charAt(0).toUpperCase() + statusRaw.trim().slice(1).toLowerCase()
      : null;
  const showStatusChip = Boolean(statusDisplay && statusDisplay.toLowerCase() !== 'scheduled');
  const visibleSessionTags = tagList.filter((t) => {
    const l = t.toLowerCase();
    return l !== 'video call' && l !== 'scheduled';
  });
  const showChipsRow = showStatusChip || visibleSessionTags.length > 0;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
      <Paper
        elevation={0}
        sx={{
          borderRadius: '12px',
          border: '1px solid #E4E7EC',
          bgcolor: '#fff',
          px: { xs: 1.5, sm: 2 },
          py: 1.25,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1.25,
          flexWrap: 'wrap',
          rowGap: 1,
        }}
      >
        <Button
          type="button"
          onClick={onBack}
          startIcon={<ArrowBackIosNewRoundedIcon sx={{ fontSize: 14 }} />}
          sx={{
            textTransform: 'none',
            fontFamily: 'Inter, sans-serif',
            fontWeight: 600,
            fontSize: '0.875rem',
            color: '#344054',
            minWidth: 0,
            px: 1,
            py: 0.75,
            borderRadius: '10px',
            alignSelf: 'center',
            '&:hover': { bgcolor: '#F2F4F7', color: '#101828' },
          }}
        >
          Back
        </Button>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            flexWrap: 'wrap',
            ml: { xs: 0, sm: 'auto' },
            justifyContent: { xs: 'flex-end', sm: 'flex-start' },
            width: { xs: '100%', sm: 'auto' },
          }}
        >
          <Button
            disableElevation
            onClick={onScheduleSession}
            sx={{
              minHeight: 38,
              px: 2,
              borderRadius: '8px',
              textTransform: 'none',
              fontFamily: 'Inter, sans-serif',
              fontWeight: 600,
              fontSize: '0.8125rem',
              bgcolor: '#3BAEDB',
              color: '#fff',
              boxShadow: 'none',
              '&:hover': { bgcolor: '#2D9DCB', boxShadow: 'none' },
            }}
          >
            Schedule session
          </Button>
          <Button
            variant="outlined"
            onClick={onViewAllSessions}
            sx={{
              minHeight: 38,
              px: 2,
              borderRadius: '8px',
              textTransform: 'none',
              fontFamily: 'Inter, sans-serif',
              fontWeight: 600,
              fontSize: '0.8125rem',
              color: '#344054',
              borderColor: '#D0D5DD',
              '&:hover': { borderColor: '#98A2B3', bgcolor: '#F9FAFB' },
            }}
          >
            View all sessions
          </Button>
        </Box>
      </Paper>

      <Paper
        elevation={0}
        sx={{
          borderRadius: '14px',
          border: '1px solid #E4E7EC',
          bgcolor: '#fff',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            px: { xs: 2, md: 2.5 },
            py: { xs: 1.75, md: 2 },
            borderBottom: '1px solid #F2F4F7',
          }}
        >
          <Typography
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 700,
              fontSize: { xs: '1.05rem', sm: '1.2rem' },
              lineHeight: 1.3,
              color: '#0F172A',
              overflowWrap: 'anywhere',
            }}
          >
            {title}
          </Typography>
        </Box>

        <Box
          sx={{
            p: { xs: 2, md: 2.5 },
            display: 'flex',
            gap: 2,
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'stretch', sm: 'flex-start' },
          }}
        >
          <Box
            sx={{
              width: 76,
              minWidth: 76,
              borderRadius: '12px',
              border: '1px solid #EAECF0',
              background: 'linear-gradient(180deg, #F8FAFC 0%, #F1F5F9 100%)',
              py: 1.35,
              px: 0.5,
              textAlign: 'center',
              alignSelf: { xs: 'center', sm: 'flex-start' },
              flexShrink: 0,
            }}
          >
            <Typography
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontWeight: 600,
                fontSize: '0.75rem',
                letterSpacing: '0.06em',
                color: '#64748B',
                textTransform: 'uppercase',
              }}
            >
              {format(start, 'EEE')}
            </Typography>
            <Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 800, fontSize: '1.85rem', color: '#0F172A', lineHeight: 1, mt: 0.5 }}>
              {format(start, 'd')}
            </Typography>
            <Typography
              sx={{
                mt: 0.35,
                fontFamily: 'Inter, sans-serif',
                fontWeight: 700,
                fontSize: '0.75rem',
                letterSpacing: '0.08em',
                color: '#94A3B8',
              }}
            >
              {format(start, 'MMM').toUpperCase()}
            </Typography>
          </Box>

          <Box sx={{ flex: 1, minWidth: 0, pt: { xs: 0, sm: 0.15 } }}>
            <Typography
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontWeight: 600,
                fontSize: '0.875rem',
                color: '#101828',
                lineHeight: 1.35,
              }}
            >
              {format(start, 'EEEE, d MMMM yyyy')}
            </Typography>
            <Typography
              sx={{
                mt: 0.5,
                fontFamily: 'Inter, sans-serif',
                fontWeight: 500,
                fontSize: '0.8125rem',
                color: '#667085',
              }}
            >
              {format(start, 'h:mm a')} – {format(end, 'h:mm a')} · {modeLabel} · {durationMin} min
            </Typography>

            {description ? (
              <Typography
                sx={{
                  mt: 1.5,
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 400,
                  fontSize: '0.875rem',
                  lineHeight: 1.55,
                  color: '#475467',
                  overflowWrap: 'anywhere',
                }}
              >
                {description}
              </Typography>
            ) : null}

            {meetingLink ? (
              <Box
                sx={{
                  mt: 1.5,
                  p: 1.25,
                  borderRadius: '10px',
                  border: '1px solid #E0ECF0',
                  bgcolor: '#F8FAFC',
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: { xs: 'stretch', sm: 'center' },
                  gap: 1,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, minWidth: 0, flex: 1 }}>
                  {isGoogleMeetLink ? (
                    <Box
                      component="img"
                      src={googleMeetIcon}
                      alt=""
                      sx={{ width: 22, height: 22, mt: 0.15, flexShrink: 0 }}
                    />
                  ) : (
                    <VideocamOutlinedIcon sx={{ fontSize: 22, color: '#64748B', mt: 0.15, flexShrink: 0 }} />
                  )}
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontFamily: 'Inter, sans-serif',
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        color: '#475467',
                        lineHeight: 1.3,
                      }}
                    >
                      {meetingPlatformLabel}
                    </Typography>
                    <Typography
                      component="a"
                      href={meetingLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        display: 'block',
                        mt: 0.35,
                        fontFamily: 'Inter, sans-serif',
                        fontWeight: 500,
                        fontSize: '0.8125rem',
                        color: '#0f8bc7',
                        lineHeight: 1.4,
                        overflowWrap: 'anywhere',
                        textDecoration: 'none',
                        '&:hover': { textDecoration: 'underline' },
                      }}
                    >
                      {meetingLink}
                    </Typography>
                  </Box>
                </Box>
                <Button
                  type="button"
                  variant="outlined"
                  size="small"
                  onClick={() => void handleCopyMeetingLink()}
                  sx={{
                    flexShrink: 0,
                    alignSelf: { xs: 'stretch', sm: 'center' },
                    minHeight: 32,
                    borderRadius: '8px',
                    textTransform: 'none',
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    color: '#344054',
                    borderColor: '#D0D5DD',
                    '&:hover': { borderColor: '#98A2B3', bgcolor: '#fff' },
                  }}
                >
                  Copy link
                </Button>
              </Box>
            ) : null}

            {showChipsRow ? (
            <Box sx={{ mt: 1.5, display: 'flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'center' }}>
              {showStatusChip ? (
                <Box
                  sx={{
                    borderRadius: '999px',
                    px: 1.2,
                    py: 0.35,
                    bgcolor: '#EFF8FF',
                    border: '1px solid #B9E6FE',
                  }}
                >
                  <Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: '0.75rem', color: '#026AA2' }}>
                    {statusDisplay}
                  </Typography>
                </Box>
              ) : null}
              {visibleSessionTags.map((tag) => (
                  <Box
                    key={tag}
                    sx={{
                      borderRadius: '999px',
                      px: 1.2,
                      py: 0.35,
                      bgcolor: '#EEF4F6',
                      border: '1px solid #E0ECF0',
                    }}
                  >
                    <Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: '0.75rem', color: '#4B6B7D' }}>
                      {formatUnderscoreDisplayLabel(tag)}
                    </Typography>
                  </Box>
                ))}
            </Box>
            ) : null}

            {!hideLifecycle ? (
              <Box
                sx={{
                  mt: 2,
                  pt: 2,
                  borderTop: '1px solid #F2F4F7',
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 0.8,
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                {!isPastSession && (
                  <Button
                    disableElevation
                    onClick={async () => {
                      try {
                        const { createRescheduleLink } = await import('@/lib/api/appointments');
                        const res = await createRescheduleLink({ appointmentId: appointmentId.toString(), expiresInDays: 1 });
                        const link = `${window.location.origin}/reschedule/${res.data.linkId}`;
                        await navigator.clipboard.writeText(link);
                        showSuccessToast('Link Copied', 'Reschedule link copied to clipboard.');
                      } catch (e) {
                        showErrorToast(e, 'Failed to create reschedule link');
                      }
                    }}
                    sx={{
                      minHeight: 30,
                      px: 1.35,
                      borderRadius: '8px',
                      textTransform: 'none',
                      fontFamily: 'Inter, sans-serif',
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      color: '#667085',
                      border: '1px solid #D0D5DD',
                      '&:hover': {
                        borderColor: '#BFC7D2',
                        bgcolor: '#F8FAFC',
                      },
                    }}
                  >
                    Copy Reschedule Link
                  </Button>
                )}
                <Box sx={{ ml: 'auto', display: 'flex', gap: 0.8 }}>
                  <Button
                    variant="outlined"
                    onClick={onCancel}
                  sx={{
                    minHeight: 30,
                    minWidth: 76,
                    px: 1.35,
                    borderRadius: '8px',
                    textTransform: 'none',
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    color: '#F04438',
                    borderColor: '#FECDCA',
                    '&:hover': {
                      borderColor: '#FDA29B',
                      bgcolor: '#FFF5F5',
                    },
                  }}
                >
                  Cancel
                </Button>
                {showJoin ? (
                  <Button
                    disableElevation
                    component="a"
                    href={meetingLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    startIcon={<VideocamOutlinedIcon sx={{ fontSize: 14 }} />}
                    sx={{
                      minHeight: 30,
                      minWidth: 88,
                      px: 1.35,
                      borderRadius: '8px',
                      textTransform: 'none',
                      fontFamily: 'Inter, sans-serif',
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      bgcolor: '#3BAEDB',
                      color: '#fff',
                      '&:hover': {
                        bgcolor: '#2D9DCB',
                      },
                      '& .MuiButton-startIcon': {
                        mr: 0.5,
                      },
                    }}
                  >
                    Join
                  </Button>
                ) : null}
                <Button
                  variant="outlined"
                  onClick={onReschedule}
                  sx={{
                    minHeight: 30,
                    minWidth: 94,
                    px: 1.35,
                    borderRadius: '8px',
                    textTransform: 'none',
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    color: '#667085',
                    borderColor: '#D0D5DD',
                    '&:hover': {
                      borderColor: '#BFC7D2',
                      bgcolor: '#F8FAFC',
                    },
                  }}
                >
                  Reschedule
                </Button>
              </Box>
            </Box>
            ) : null}
          </Box>
        </Box>
      </Paper>

      <Paper
        elevation={0}
        sx={{
          borderRadius: '14px',
          border: '1px solid #E4E7EC',
          bgcolor: '#fff',
          p: { xs: 2, md: 2.5 },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1.5,
            mb: 1,
            flexWrap: 'wrap',
          }}
        >
          <Typography
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 700,
              fontSize: '0.95rem',
              color: '#0F172A',
              lineHeight: 1.3,
            }}
          >
            Session notes
          </Typography>
          <Button
            type="button"
            size="small"
            variant="text"
            onClick={() => {
              setNoteFeedback('');
              setAddNoteOpen(true);
            }}
            sx={{
              textTransform: 'none',
              fontFamily: 'Inter, sans-serif',
              fontWeight: 600,
              fontSize: '0.8125rem',
              color: '#0f8bc7',
              minHeight: 32,
              px: 1,
              flexShrink: 0,
            }}
          >
            + Add note
          </Button>
        </Box>

        {sortedNotes.length === 0 ? (
          <Box
            sx={{
              py: 3,
              px: 2,
              borderRadius: '12px',
              border: '1px dashed #D0D5DD',
              bgcolor: '#FAFAFA',
              textAlign: 'center',
            }}
          >
            <EventNoteOutlinedIcon sx={{ fontSize: 36, color: '#98A2B3', mb: 1 }} />
            <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.875rem', fontWeight: 600, color: '#475467' }}>
              No notes yet
            </Typography>
            <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.8125rem', color: '#98A2B3', mt: 0.5 }}>
              Add a note to capture decisions or follow-ups from this session.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
            {sortedNotes.map((note) => (
              <Paper
                key={note.id}
                elevation={0}
                sx={{
                  borderRadius: '10px',
                  border: '1px solid #EAECF0',
                  bgcolor: '#FCFCFD',
                  p: 1.35,
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'baseline',
                    justifyContent: 'space-between',
                    columnGap: 1,
                    rowGap: 0.25,
                    mb: 0.75,
                  }}
                >
                  <Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: '0.78rem', color: '#475467' }}>
                    {note.authorName || 'Nutritionist'}
                  </Typography>
                  {note.createdAt ? (
                    <Typography
                      sx={{
                        fontFamily: 'Inter, sans-serif',
                        fontSize: '0.75rem',
                        color: '#98A2B3',
                        ml: 'auto',
                        textAlign: 'right',
                      }}
                    >
                      {formatNoteTimestamp(note.createdAt)}
                    </Typography>
                  ) : null}
                </Box>
                <Typography
                  sx={{
                    fontFamily: 'Inter, sans-serif',
                    fontSize: '0.86rem',
                    lineHeight: 1.45,
                    color: '#1D2939',
                    whiteSpace: 'pre-wrap',
                    overflowWrap: 'anywhere',
                  }}
                >
                  {note.text}
                </Typography>
              </Paper>
            ))}
          </Box>
        )}
      </Paper>

      <Dialog
        open={addNoteOpen}
        onClose={() => {
          if (!savingNote) {
            setNoteFeedback('');
            setAddNoteOpen(false);
          }
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: '14px' },
        }}
      >
        <DialogTitle sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 700, fontSize: '1.05rem', color: '#0F172A' }}>
          Add session note
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={4}
            placeholder="e.g. Client requested to focus on high-protein meal options during the next session."
            value={noteDraft}
            onChange={(e) => setNoteDraft(e.target.value)}
            disabled={savingNote}
            sx={{
              mt: 0.5,
              '& .MuiOutlinedInput-root': {
                fontFamily: 'Inter, sans-serif',
                fontSize: '0.88rem',
              },
            }}
          />
          {noteFeedback ? (
            <Box sx={{ mt: 1.5 }}>
              <InlineFeedback
                variant="error"
                title="Failed to save note"
                message={noteFeedback}
              />
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button
            type="button"
            disabled={savingNote}
            onClick={() => {
              setAddNoteOpen(false);
              setNoteDraft('');
              setNoteFeedback('');
            }}
            sx={{ textTransform: 'none', fontFamily: 'Inter, sans-serif', fontWeight: 600, color: '#667085' }}
          >
            Cancel
          </Button>
          <Button
            type="button"
            disableElevation
            disabled={savingNote || !noteDraft.trim()}
            onClick={() => void handleSaveNote()}
            sx={{
              textTransform: 'none',
              fontFamily: 'Inter, sans-serif',
              fontWeight: 600,
              bgcolor: '#3BAEDB',
              color: '#fff',
              px: 2.5,
              '&:hover': { bgcolor: '#2D9DCB' },
              '&:disabled': { bgcolor: '#E4E7EC', color: '#98A2B3' },
            }}
          >
            {savingNote ? 'Saving…' : 'Save note'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
